/**
 * CommandPalette.jsx — Cmd+K global command palette
 *
 * Triggered via Cmd+K (Mac) / Ctrl+K (Windows).
 * Provides navigation, quick actions, and live search results.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/contexts/ThemeContext";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Home,
  Calendar,
  ClipboardList,
  BookOpen,
  ShoppingCart,
  Settings,
  Activity,
  Camera,
  Truck,
  Sparkles,
  Sun,
  Moon,
  LogOut,
  Search,
  UtensilsCrossed,
  Apple,
  FileText,
  Zap,
} from "lucide-react";

const API = "";

const NAV_ITEMS = [
  { id: "nav-dashboard", label: "Home", path: "/dashboard", icon: Home, keywords: "dashboard home overview" },
  { id: "nav-meal-plan", label: "Meal Plan", path: "/meal-plan", icon: Calendar, keywords: "meal plan weekly" },
  { id: "nav-hybrid", label: "Hybrid Planner", path: "/hybrid-planner", icon: Sparkles, keywords: "hybrid planner ai" },
  { id: "nav-food-log", label: "Nutrition Tracker", path: "/food-log", icon: ClipboardList, keywords: "food log nutrition tracker" },
  { id: "nav-recipes", label: "Recipes", path: "/recipes", icon: BookOpen, keywords: "recipes cookbook" },
  { id: "nav-snap", label: "Snap & Cook", path: "/snap-meal", icon: Camera, keywords: "camera snap photo scan barcode" },
  { id: "nav-activity", label: "Activity", path: "/activity", icon: Activity, keywords: "activity workout exercise" },
  { id: "nav-grocery", label: "Shop My Plan", path: "/grocery-list", icon: ShoppingCart, keywords: "grocery shopping list shop my plan" },
  { id: "nav-delivery", label: "Meal Delivery", path: "/meal-delivery", icon: Truck, keywords: "delivery order" },
  { id: "nav-settings", label: "Settings", path: "/settings", icon: Settings, keywords: "settings preferences profile" },
];

const ACTION_ITEMS = [
  { id: "act-snap", label: "Snap a meal", path: "/snap-meal", icon: Camera, keywords: "snap photo capture scan" },
  { id: "act-log", label: "Add meal manually", path: "/food-log", icon: UtensilsCrossed, keywords: "add meal manual entry" },
  { id: "act-plan", label: "Generate meal plan", path: "/meal-plan", icon: Zap, keywords: "generate create meal plan ai" },
];

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const debounceRef = useRef(null);

  // Global keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query || query.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/search/global?q=${encodeURIComponent(query)}`, {
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.results || []);
        }
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const runAction = useCallback(
    (path) => {
      setOpen(false);
      setQuery("");
      setSearchResults([]);
      navigate(path);
    },
    [navigate]
  );

  const handleThemeToggle = useCallback(() => {
    toggleTheme();
    setOpen(false);
    setQuery("");
  }, [toggleTheme]);

  const handleLogout = useCallback(async () => {
    setOpen(false);
    setQuery("");
    await logout();
    navigate("/login");
  }, [logout, navigate]);

  const handleSearchSelect = useCallback(
    (result) => {
      setOpen(false);
      setQuery("");
      setSearchResults([]);
      if (result.type === "recipe") navigate("/recipes");
      else if (result.type === "food_log") navigate("/food-log");
      else if (result.type === "meal_plan") navigate("/meal-plan");
    },
    [navigate]
  );

  const typeIcon = (type) => {
    if (type === "recipe") return BookOpen;
    if (type === "food_log") return Apple;
    if (type === "meal_plan") return FileText;
    return Search;
  };

  if (!user) return null;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <DialogTitle className="sr-only">Command Palette</DialogTitle>
      <DialogDescription className="sr-only">Search pages, meals, and actions</DialogDescription>
      <div className="cmdk-palette" data-testid="command-palette">
        <CommandInput
          placeholder="Search pages, meals, actions..."
          value={query}
          onValueChange={setQuery}
          data-testid="command-palette-input"
        />
        <CommandList>
          <CommandEmpty>
            {searching ? "Searching..." : "No results found."}
          </CommandEmpty>

          {/* Live search results */}
          {searchResults.length > 0 && (
            <CommandGroup heading="Search Results">
              {searchResults.map((r) => {
                const Icon = typeIcon(r.type);
                return (
                  <CommandItem
                    key={`${r.type}-${r.id}`}
                    value={`${r.title} ${r.type}`}
                    onSelect={() => handleSearchSelect(r)}
                    data-testid={`command-result-${r.type}-${r.id}`}
                  >
                    <Icon className="mr-2 h-4 w-4 opacity-60" />
                    <div className="flex flex-col">
                      <span>{r.title}</span>
                      <span className="text-xs opacity-50">{r.subtitle}</span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          {/* Quick Actions */}
          <CommandGroup heading="Quick Actions">
            {ACTION_ITEMS.map((item) => (
              <CommandItem
                key={item.id}
                value={`${item.label} ${item.keywords}`}
                onSelect={() => runAction(item.path)}
                data-testid={`command-action-${item.id}`}
              >
                <item.icon className="mr-2 h-4 w-4 opacity-60" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />

          {/* Navigation */}
          <CommandGroup heading="Navigate">
            {NAV_ITEMS.map((item) => (
              <CommandItem
                key={item.id}
                value={`${item.label} ${item.keywords}`}
                onSelect={() => runAction(item.path)}
                data-testid={`command-nav-${item.id}`}
              >
                <item.icon className="mr-2 h-4 w-4 opacity-60" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />

          {/* Preferences */}
          <CommandGroup heading="Preferences">
            <CommandItem
              value="toggle theme dark light mode"
              onSelect={handleThemeToggle}
              data-testid="command-pref-theme"
            >
              {theme === "dark" ? (
                <Sun className="mr-2 h-4 w-4 opacity-60" />
              ) : (
                <Moon className="mr-2 h-4 w-4 opacity-60" />
              )}
              <span>{theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}</span>
              <CommandShortcut>Theme</CommandShortcut>
            </CommandItem>
            <CommandItem
              value="logout sign out"
              onSelect={handleLogout}
              data-testid="command-pref-logout"
            >
              <LogOut className="mr-2 h-4 w-4 opacity-60" />
              <span>Log out</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </div>
    </CommandDialog>
  );
};
